import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { debounce, isEqual } from 'lodash'
import type { SortConfig } from '../utils/sorting'

export interface PaginatedData<T> {
  items: T[]
  total_count: number
}

export interface FilteredTableFilters {
  toggle: string
  page: number
  limit: number
  searchParam: string
  featureFlagParam: string | null
  featureFlagInverted: boolean
  sortKey: string | null
  sortDirection: 'asc' | 'desc'
  [key: string]: any
}

interface UseFilteredTableQueryParams<T> {
  queryKeyFn: (filters: FilteredTableFilters) => readonly any[]
  queryFn: (
    filters: FilteredTableFilters,
    signal?: AbortSignal
  ) => Promise<PaginatedData<T>>
  defaultToggle: string
  defaultPage?: number
  defaultRowsPerPage?: number
  debounceMs?: number
  additionalToggles?: Record<string, any>
  enabled?: boolean
  defaultSortKey?: string | null
  defaultSortDirection?: 'asc' | 'desc'
  syncWithUrl?: boolean
}

export interface UseFilteredTableQueryResult<T> {
  // Query state
  data: T[]
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  error: Error | null
  refetch: () => void

  // Pagination state
  page: number
  rowsPerPage: number
  totalCount: number
  handlePageChange: (newPage: number) => void
  handleRowsPerPageChange: (value: string) => void

  // Search state
  inputValue: string
  searchParam: string
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void

  // Toggle state
  toggleMenu: string
  handleToggleChange: (newToggle: string) => void

  // Additional toggles
  additionalToggleValues: Record<string, any>
  handleAdditionalToggleChange: (toggleName: string, newValue: any) => void

  // Feature flag filter
  featureFlagFilter: string | null
  handleFeatureFlagChange: (newValue: string | null) => void

  // Feature flag inverted
  featureFlagInverted: boolean
  handleFeatureFlagInvertedChange: (inverted: boolean) => void

  // Clears search, toggles and the flag filter; keeps sort and page size
  handleResetFilters: (toggle?: string) => void

  // Sort state
  sortConfig: SortConfig
  handleSort: (key: string) => void
}

export const useFilteredTableQuery = <T>({
  queryKeyFn,
  queryFn,
  defaultToggle,
  defaultPage = 0,
  defaultRowsPerPage = 25,
  debounceMs = 350,
  additionalToggles = {},
  enabled = true,
  defaultSortKey = null,
  defaultSortDirection = 'asc',
  syncWithUrl = false
}: UseFilteredTableQueryParams<T>): UseFilteredTableQueryResult<T> => {
  const [searchParams, setSearchParams] = useSearchParams()
  const prevParamsRef = useRef<string | null>(null)
  const additionalTogglesRef = useRef(additionalToggles)
  if (!isEqual(additionalTogglesRef.current, additionalToggles)) {
    additionalTogglesRef.current = additionalToggles
  }

  const getInitialRowsPerPage = () => {
    const stored = localStorage.getItem('pageSize')
    if (stored) {
      const parsed = parseInt(stored, 10)
      if ([10, 25, 50, 100].includes(parsed)) {
        return parsed
      }
    }
    return defaultRowsPerPage
  }

  // Lazy initializers read URL params once on mount (not every render)
  const [inputValue, setInputValue] = useState(() =>
    syncWithUrl ? (searchParams.get('search') || '').trim() : ''
  )
  const [searchParam, setSearchParam] = useState(() =>
    syncWithUrl ? (searchParams.get('search') || '').trim() : ''
  )
  const [toggleMenu, setToggleMenu] = useState(() =>
    syncWithUrl ? searchParams.get('toggle') || defaultToggle : defaultToggle
  )
  const [page, setPage] = useState(() => {
    if (!syncWithUrl) {
      return defaultPage
    }
    const parsed = parseInt(searchParams.get('page') || '0', 10)
    return Number.isNaN(parsed) || parsed < 0 ? defaultPage : parsed
  })
  const [rowsPerPage, setRowsPerPage] = useState(getInitialRowsPerPage)
  const [additionalToggleValues, setAdditionalToggleValues] = useState(() => {
    if (!syncWithUrl) {
      return additionalToggles
    }
    const result = { ...additionalToggles }
    for (const key of Object.keys(additionalToggles)) {
      const urlVal = searchParams.get(key)
      if (urlVal) {
        result[key] = urlVal
      }
    }
    return result
  })
  const [featureFlagFilter, setFeatureFlagFilter] = useState<string | null>(
    () => (syncWithUrl ? searchParams.get('ff') || null : null)
  )
  const [featureFlagInverted, setFeatureFlagInverted] = useState(() =>
    syncWithUrl ? searchParams.get('ffInverted') === 'true' : false
  )
  const [sortKey, setSortKey] = useState<string | null>(() =>
    syncWithUrl ? searchParams.get('sortKey') || defaultSortKey : defaultSortKey
  )
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    if (!syncWithUrl) {
      return defaultSortDirection
    }
    const dir = searchParams.get('sortDir')
    return dir === 'asc' || dir === 'desc' ? dir : defaultSortDirection
  })

  const filters: FilteredTableFilters = useMemo(
    () => ({
      toggle: toggleMenu,
      page: page + 1, // API uses 1-based
      limit: rowsPerPage,
      searchParam,
      featureFlagParam: featureFlagFilter,
      featureFlagInverted,
      sortKey,
      sortDirection,
      ...additionalToggleValues
    }),
    [
      toggleMenu,
      page,
      rowsPerPage,
      searchParam,
      featureFlagFilter,
      featureFlagInverted,
      sortKey,
      sortDirection,
      additionalToggleValues
    ]
  )

  const query: UseQueryResult<PaginatedData<T>> = useQuery({
    queryKey: queryKeyFn(filters),
    queryFn: ({ signal }) => queryFn(filters, signal),
    placeholderData: prev => prev,
    enabled
  })

  // Debounced search update
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        // Trim so pasted values with stray whitespace still match (e.g. org IDs)
        setSearchParam(value.trim())
        setPage(0)
      }, debounceMs),
    [debounceMs]
  )

  // Cleanup debounce on unmount
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

  // Sync state → URL when syncWithUrl is enabled
  useEffect(() => {
    if (!syncWithUrl) {
      return
    }
    const params = new URLSearchParams()
    if (searchParam) {
      params.set('search', searchParam)
    }
    if (toggleMenu !== defaultToggle) {
      params.set('toggle', toggleMenu)
    }
    if (page !== 0) {
      params.set('page', String(page))
    }
    for (const [key, value] of Object.entries(additionalToggleValues)) {
      if (value && value !== additionalTogglesRef.current[key]) {
        params.set(key, String(value))
      }
    }
    if (featureFlagFilter) {
      params.set('ff', featureFlagFilter)
    }
    if (featureFlagInverted) {
      params.set('ffInverted', 'true')
    }
    if (sortKey && sortKey !== defaultSortKey) {
      params.set('sortKey', sortKey)
    }
    if (sortDirection !== defaultSortDirection) {
      params.set('sortDir', sortDirection)
    }
    // Skip no-op updates to avoid unnecessary re-renders
    const nextStr = params.toString()
    if (nextStr === prevParamsRef.current) {
      return
    }
    prevParamsRef.current = nextStr
    setSearchParams(params, { replace: true })
  }, [
    syncWithUrl,
    searchParam,
    toggleMenu,
    page,
    additionalToggleValues,
    featureFlagFilter,
    featureFlagInverted,
    sortKey,
    sortDirection,
    defaultToggle,
    defaultSortKey,
    defaultSortDirection,
    setSearchParams
  ])

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value
      setInputValue(newValue)
      debouncedSetSearch(newValue)
    },
    [debouncedSetSearch]
  )

  const handleToggleChange = useCallback((newToggle: string) => {
    setToggleMenu(newToggle)
    setPage(0)
  }, [])

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage)
  }, [])

  const handleRowsPerPageChange = useCallback((value: string) => {
    const newSize = parseInt(value, 10)
    setRowsPerPage(newSize)
    setPage(0)
    localStorage.setItem('pageSize', value)
  }, [])

  const handleAdditionalToggleChange = useCallback(
    (toggleName: string, newValue: any) => {
      setAdditionalToggleValues(prev => ({
        ...prev,
        [toggleName]: newValue
      }))
      setPage(0)
    },
    []
  )

  const handleFeatureFlagChange = useCallback((newValue: string | null) => {
    setFeatureFlagFilter(newValue)
    if (newValue === null) {
      setFeatureFlagInverted(false)
    }
    setPage(0) // Reset to first page
  }, [])

  const handleFeatureFlagInvertedChange = useCallback((inverted: boolean) => {
    setFeatureFlagInverted(inverted)
    setPage(0)
  }, [])

  const handleResetFilters = useCallback(
    (toggle: string = defaultToggle) => {
      // A pending keystroke would otherwise re-apply the old search after the reset
      debouncedSetSearch.cancel()
      setInputValue('')
      setSearchParam('')
      setToggleMenu(toggle)
      setAdditionalToggleValues(additionalTogglesRef.current)
      setFeatureFlagFilter(null)
      setFeatureFlagInverted(false)
      setPage(0)
    },
    [debouncedSetSearch, defaultToggle]
  )

  const handleSort = useCallback(
    (key: string) => {
      setSortDirection(prev =>
        sortKey === key && prev === 'asc' ? 'desc' : 'asc'
      )
      setSortKey(key)
      setPage(0)
    },
    [sortKey]
  )

  const sortConfigMemo: SortConfig = useMemo(
    () => ({ key: sortKey, direction: sortDirection }),
    [sortKey, sortDirection]
  )

  // Extract total count from response structure: { items: [...], total_count: 123 }
  const totalCount = useMemo(() => {
    return query.data?.total_count || 0
  }, [query.data])

  // Extract items array from response structure: { items: [...], total_count: 123 }
  const items = useMemo(() => {
    return query.data?.items || []
  }, [query.data])

  return {
    // Query state
    data: items,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,

    // Pagination state
    page,
    rowsPerPage,
    totalCount,
    handlePageChange,
    handleRowsPerPageChange,

    // Search state
    inputValue,
    searchParam,
    handleSearchChange,

    // Toggle state
    toggleMenu,
    handleToggleChange,

    // Additional toggles
    additionalToggleValues,
    handleAdditionalToggleChange,

    // Feature flag filter
    featureFlagFilter,
    handleFeatureFlagChange,

    // Feature flag inverted
    featureFlagInverted,
    handleFeatureFlagInvertedChange,

    handleResetFilters,

    // Sort state
    sortConfig: sortConfigMemo,
    handleSort
  }
}
